import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Filter, Search, UserPlus, Users, X } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/PageHeader";
import { DataTable } from "../../components/DataTable";
import { EmptyState, TableSkeleton } from "../../components/EmptyState";
import { LevelBadge } from "../../components/Badges";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { api, apiError, mediaUrl } from "../../lib/api";
import { formatINR, formatPct, initials } from "../../lib/format";

const LEVELS = ["Bronze", "Silver", "Gold", "Platinum", "Diamond"];
const EMPTY = { search: "", managerId: "all", status: "all", level: "all", minInvested: "", maxInvested: "" };

export default function AdminClients() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [managers, setManagers] = useState([]);
  const [state, setState] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    api
      .get("/api/admin/managers")
      .then(({ data }) => setManagers(data))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (applied.search) params.search = applied.search;
      if (applied.managerId !== "all") params.managerId = applied.managerId;
      if (applied.status !== "all") params.status = applied.status;
      if (applied.level !== "all") params.level = applied.level;
      if (applied.minInvested !== "") params.minInvested = Number(applied.minInvested);
      if (applied.maxInvested !== "") params.maxInvested = Number(applied.maxInvested);
      const { data } = await api.get("/api/admin/clients", { params });
      setState(data);
    } catch (error) {
      toast.error(apiError(error, "Could not load clients"));
    } finally {
      setLoading(false);
    }
  }, [page, applied]);

  useEffect(() => {
    load();
  }, [load]);

  const activeFilterCount = useMemo(
    () =>
      Object.entries(applied).filter(
        ([k, v]) => v !== "" && v !== "all" && !(k === "search" && !v)
      ).length,
    [applied]
  );

  const apply = () => {
    setPage(1);
    setApplied(filters);
    setShowFilters(false);
  };

  const clearAll = () => {
    setFilters(EMPTY);
    setApplied(EMPTY);
    setPage(1);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Client",
        cell: ({ row }) => {
          const c = row.original;
          const photo = mediaUrl(c.photo);
          return (
            <Link
              to={`/admin/clients/${c.id}`}
              className="flex items-center gap-3"
              data-testid={`admin-client-link-${c.id}`}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-secondary text-[11px] font-semibold">
                {photo ? (
                  <img src={photo} alt={c.name} className="h-full w-full object-cover" />
                ) : (
                  initials(c.name)
                )}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium hover:underline">{c.name}</span>
                <span className="block truncate text-xs text-muted-foreground">{c.email}</span>
              </span>
            </Link>
          );
        },
      },
      {
        accessorKey: "managerName",
        header: "Manager",
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.managerName || "Unassigned"}
          </span>
        ),
      },
      {
        accessorKey: "level",
        header: "Level",
        cell: ({ row }) => <LevelBadge level={row.original.level} />,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <span
            className={
              row.original.status === "active"
                ? "inline-flex items-center rounded-full border border-[hsl(var(--profit))]/30 bg-[hsl(var(--profit))]/15 px-2.5 py-0.5 text-xs font-medium text-[hsl(var(--profit))]"
                : "inline-flex items-center rounded-full border border-destructive/30 bg-destructive/15 px-2.5 py-0.5 text-xs font-medium text-destructive"
            }
          >
            {row.original.status === "active" ? "Active" : "Suspended"}
          </span>
        ),
      },
      {
        accessorKey: "totalInvested",
        header: "Invested",
        meta: { numeric: true },
        cell: ({ row }) => formatINR(row.original.totalInvested),
      },
      {
        accessorKey: "dailyProfitAmount",
        header: "Daily profit",
        meta: { numeric: true },
        cell: ({ row }) => (
          <span className="text-[hsl(var(--profit))]">{formatINR(row.original.dailyProfitAmount)}</span>
        ),
      },
      {
        accessorKey: "overallProfitPercent",
        header: "Overall %",
        meta: { numeric: true },
        cell: ({ row }) => formatPct(row.original.overallProfitPercent),
      },
    ],
    []
  );

  const filterPanel = (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <div className="space-y-2">
        <Label>Manager</Label>
        <Select
          value={filters.managerId}
          onValueChange={(v) => setFilters((f) => ({ ...f, managerId: v }))}
        >
          <SelectTrigger data-testid="admin-clients-manager-filter">
            <SelectValue placeholder="All managers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All managers</SelectItem>
            {managers.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Status</Label>
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
          <SelectTrigger data-testid="admin-clients-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Level</Label>
        <Select value={filters.level} onValueChange={(v) => setFilters((f) => ({ ...f, level: v }))}>
          <SelectTrigger data-testid="admin-clients-level-filter">
            <SelectValue placeholder="All levels" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All levels</SelectItem>
            {LEVELS.map((l) => (
              <SelectItem key={l} value={l}>
                {l}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Invested amount range</Label>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            placeholder="Min"
            value={filters.minInvested}
            onChange={(e) => setFilters((f) => ({ ...f, minInvested: e.target.value }))}
            data-testid="admin-clients-min-invested"
          />
          <Input
            type="number"
            placeholder="Max"
            value={filters.maxInvested}
            onChange={(e) => setFilters((f) => ({ ...f, maxInvested: e.target.value }))}
            data-testid="admin-clients-max-invested"
          />
        </div>
      </div>
      <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-4">
        <Button onClick={apply} data-testid="admin-clients-apply-filters">
          Apply filters
        </Button>
        <Button variant="ghost" onClick={clearAll} data-testid="admin-clients-clear-filters">
          <X className="mr-2 h-4 w-4" /> Clear
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      <PageHeader
        title="Clients"
        subtitle={`${state.total} client${state.total === 1 ? "" : "s"} on the platform`}
        actions={
          <Button onClick={() => navigate("/admin/clients/new")} data-testid="admin-clients-create-button">
            <UserPlus className="mr-2 h-4 w-4" /> New client
          </Button>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <form
          className="relative flex-1"
          onSubmit={(e) => {
            e.preventDefault();
            apply();
          }}
        >
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search by name, email or phone"
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            data-testid="admin-clients-search-input"
          />
        </form>
        <div className="flex gap-2">
          <Button onClick={apply} variant="secondary" data-testid="admin-clients-search-button">
            Search
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowFilters((s) => !s)}
            data-testid="admin-clients-filters-open-button"
          >
            <Filter className="mr-2 h-4 w-4" />
            Filters
            {activeFilterCount > 0 ? (
              <span className="ml-2 rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
                {activeFilterCount}
              </span>
            ) : null}
          </Button>
        </div>
      </div>

      {showFilters ? (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="admin-clients-filter-panel">
          {filterPanel}
        </div>
      ) : null}

      {loading ? (
        <TableSkeleton cols={6} />
      ) : state.items.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No clients found"
          description={
            activeFilterCount > 0
              ? "No clients match the current filters. Try widening your search."
              : "Create the first investor account to get started."
          }
          action={
            <Button onClick={() => navigate("/admin/clients/new")} data-testid="admin-clients-empty-create">
              <UserPlus className="mr-2 h-4 w-4" /> Create client
            </Button>
          }
          testId="admin-clients-empty-state"
        />
      ) : (
        <DataTable
          columns={columns}
          data={state.items}
          page={page}
          pages={state.pages}
          total={state.total}
          onPageChange={setPage}
          testId="admin-clients-table"
          rowTestId="admin-client-row"
        />
      )}
    </div>
  );
}
